import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import EventFlyer from "./components/EventFlyer";
import { COMMUNITY_INFORMATION_AGENT_ENABLED } from "./communityInformation/config";

function parseCsv(text) {
  // Simple CSV parser (handles quoted commas in a basic way)
  const rows = [];
  let current = "";
  let inQuotes = false;
  const line = [];
  const pushCell = () => {
    line.push(current);
    current = "";
  };

  const pushLine = () => {
    rows.push([...line]);
    line.length = 0;
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (ch === '"' && next === '"') {
      current += '"';
      i++;
      continue;
    }

    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (!inQuotes && ch === ",") {
      pushCell();
      continue;
    }

    if (!inQuotes && (ch === "\n" || ch === "\r")) {
      // handle CRLF / LF
      if (ch === "\r" && next === "\n") i++;
      pushCell();
      pushLine();
      continue;
    }

    current += ch;
  }

  // last cell
  pushCell();
  // avoid adding empty trailing line
  if (line.some((c) => c.trim() !== "")) pushLine();

  return rows;
}

function toBool(v) {
  if (v === true || v === false) return v;
  const s = String(v || "").trim().toLowerCase();
  return ["1", "true", "yes", "y"].includes(s);
}

function isDirectoryCollection(collectionName) {
  return ["directoryServices", "directorySubmissions"].includes(collectionName);
}

function Card({ title, children }) {
  return (
    <section className="bg-black/40 border border-white/10 rounded-xl p-4">
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      {children}
    </section>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceSubmissions, setServiceSubmissions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [docs, setDocs] = useState([]);
  const [foodUpdates, setFoodUpdates] = useState([]);

const [posts, setPosts] = useState([]);
const [newPostTitle, setNewPostTitle] = useState("");
const [newPostBody, setNewPostBody] = useState("");
const [addingPost, setAddingPost] = useState(false);


  const [editing, setEditing] = useState(null); // { collectionName, id, data }
  const [editData, setEditData] = useState({});

  // Bulk import state
  const [csvTarget, setCsvTarget] = useState("directoryServices");
  const [csvFileName, setCsvFileName] = useState("");
  const [csvPreview, setCsvPreview] = useState([]); // array of objects
  const [csvImportAsApproved, setCsvImportAsApproved] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;

    const eventsUnsub = onSnapshot(collection(db, "events"), (snapshot) => {
      setEvents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const servicesUnsub = onSnapshot(collection(db, "directoryServices"), (snapshot) => {
      setServices(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const serviceSubmissionsUnsub = onSnapshot(collection(db, "directorySubmissions"), (snapshot) => {
      setServiceSubmissions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const locationsUnsub = onSnapshot(collection(db, "mapLocations"), (snapshot) => {
      setLocations(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const docsUnsub = onSnapshot(collection(db, "eventDocuments"), (snapshot) => {
      setDocs(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const foodUnsub = onSnapshot(collection(db, "foodBankUpdates"), (snapshot) => {
      setFoodUpdates(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    

    const postsUnsub = onSnapshot(collection(db, "posts"), (snapshot) => {
      setPosts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
return () => {
      eventsUnsub();
      servicesUnsub();
      serviceSubmissionsUnsub();
      locationsUnsub();
      docsUnsub();
      foodUnsub();
      postsUnsub();
    };
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setEmail("");
      setPassword("");
    } catch (err) {
      console.error(err);
      setError("Login failed. Check your credentials.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleDelete = async (collectionName, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await deleteDoc(doc(db, collectionName, id));
    } catch (err) {
      console.error("Error deleting document", err);
      alert("Delete failed. Check console for details.");
    }
  };

  const handleApprove = async (collectionName, id) => {
    try {
      await updateDoc(doc(db, collectionName, id), {
        status: "approved",
        approvedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error approving", err);
      alert("Approve failed. Check console for details.");
    }
  };

  const startEdit = (collectionName, item) => {
    setEditing({ collectionName, id: item.id });
    setEditData({ ...item });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditData({});
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { collectionName, id } = editing;

    const payload = { ...editData };
    delete payload.id;

    // Coerce some common fields
    if (collectionName === "mapLocations") {
      // allow editing lat/lng in UI
      if (payload.lat != null && payload.lng != null) {
        payload.position = { lat: Number(payload.lat), lng: Number(payload.lng) };
        delete payload.lat;
        delete payload.lng;
      }
    }
    if (isDirectoryCollection(collectionName)) {
      payload.promoted = toBool(payload.promoted);
      payload.isLocal = toBool(payload.isLocal);
    }

    try {
      await updateDoc(doc(db, collectionName, id), payload);
      cancelEdit();
    } catch (err) {
      console.error("Error saving edit", err);
      alert("Save failed. Check console for details.");
    }
  };

  const pendingCounts = useMemo(() => {
  const count = (arr) =>
    arr.filter((i) => (i.status || "pending") !== "approved").length;
  return {
    events: count(events),
    services: count(services),
    serviceSubmissions: count(serviceSubmissions),
    locations: count(locations),
    docs: count(docs),
    food: count(foodUpdates),
    posts: count(posts),
  };
}, [events, services, serviceSubmissions, locations, docs, foodUpdates, posts]);

const handleCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text).filter((r) => r.some((c) => String(c).trim() !== ""));
    if (rows.length < 2) {
      alert("CSV needs a header row and at least 1 data row.");
      return;
    }
    const headers = rows[0].map((h) => String(h).trim());
    const objects = rows.slice(1).map((r) => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = r[idx] != null ? String(r[idx]).trim() : "";
      });
      return obj;
    });

    // light normalisation
    const normalised = objects.map((o) => {
      if (csvTarget === "directoryServices") {
        return {
          name: o.name || "",
          description: o.description || "",
          type: o.type || "Community",
          website: o.website || "",
          contactEmail: o.contactEmail || "",
          contactPhone: o.contactPhone || "",
          isLocal: o.isLocal === "" ? true : toBool(o.isLocal),
          promoted: o.promoted === "" ? false : toBool(o.promoted),
        };
      }
      if (csvTarget === "mapLocations") {
        const lat = Number(o.lat || o.latitude || "");
        const lng = Number(o.lng || o.longitude || "");
        return {
          name: o.name || "",
          description: o.description || "",
          keywords: o.keywords || "",
          serviceType: o.serviceType || o.type || "Community",
          postcode: o.postcode || "",
          website: o.website || "",
          contactEmail: o.contactEmail || "",
          contactPhone: o.contactPhone || "",
          position: { lat: Number.isFinite(lat) ? lat : 0, lng: Number.isFinite(lng) ? lng : 0 },
        };
      }
      // events
      const eventDate = o.date
        ? new Date(`${o.date}T${o.time || "00:00"}`)
        : null;
      return {
        title: o.title || o.name || "",
        name: o.name || o.title || "",
        description: o.description || "",
        date: o.date || "",
        time: o.time || "",
        eventDate:
          eventDate && !Number.isNaN(eventDate.getTime()) ? eventDate : null,
      };
    });

    setCsvFileName(file.name);
    setCsvPreview(normalised.slice(0, 25)); // show first 25
    // store full set in window temp (simple approach)
    window.__uunCsvFull = normalised;
  };

  const runCsvImport = async () => {
    const items = window.__uunCsvFull || [];
    if (!items.length) {
      alert("No CSV loaded.");
      return;
    }
    if (!window.confirm(`Import ${items.length} rows into ${csvTarget}?`)) return;

    setImporting(true);
    try {
      for (const item of items) {
        const payload = {
          ...item,
          status: csvImportAsApproved ? "approved" : "pending",
          createdAt: new Date().toISOString(),
          importedBy: user?.email || "admin",
        };
        await addDoc(collection(db, csvTarget), payload);
      }
      setCsvFileName("");
      setCsvPreview([]);
      window.__uunCsvFull = [];
      alert("Import complete.");
    } catch (err) {
      console.error("CSV import failed", err);
      alert("Import failed. Check console for details.");
    } finally {
      setImporting(false);
    }
  };

  if (!user) {
    return (
      <div className="text-white max-w-md mx-auto">
        <h1 className="text-3xl font-bold mb-4">Admin login</h1>
        <p className="text-gray-300 mb-4 text-sm">
          Sign in with a Firebase Auth admin account. This prototype treats any authenticated user as an admin.
        </p>
        <form onSubmit={handleLogin} className="space-y-3">
          <div>
            <label className="block text-sm mb-1" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded px-3 py-2 bg-white text-black"
            />
          </div>
          <div>
            <label className="block text-sm mb-1" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded px-3 py-2 bg-white text-black"
            />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button
            type="submit"
            className="px-4 py-2 rounded bg-[#E63946] text-white text-sm font-semibold hover:bg-[#f25561]"
          >
            Log in
          </button>
        </form>
      </div>
    );
  }

  const renderEditPanel = () => {
    if (!editing) return null;
    const c = editing.collectionName;

    return (
      <div className="bg-black/60 border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold">Editing: {c}</h3>
          <button
            type="button"
            onClick={cancelEdit}
            className="text-xs px-3 py-1 rounded-full bg-white/10 hover:bg-white/20"
          >
            Close
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs mb-1">{c === "events" ? "Title" : "Name"}</label>
            <input
              value={c === "events" ? editData.title || "" : editData.name || ""}
              onChange={(e) =>
                setEditData((p) =>
                  c === "events"
                    ? { ...p, title: e.target.value }
                    : { ...p, name: e.target.value }
                )
              }
              className="w-full rounded px-3 py-2 bg-white text-black text-sm"
            />
          </div>

          {isDirectoryCollection(c) && (
            <div>
              <label className="block text-xs mb-1">Type</label>
              <input
                value={editData.type || ""}
                onChange={(e) => setEditData((p) => ({ ...p, type: e.target.value }))}
                className="w-full rounded px-3 py-2 bg-white text-black text-sm"
              />
            </div>
          )}

          {c === "events" && (
            <>
              <div>
                <label className="block text-xs mb-1">Date (YYYY-MM-DD)</label>
                <input
                  value={editData.date || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, date: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Time</label>
                <input
                  value={editData.time || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, time: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
            </>
          )}

          {c === "mapLocations" && (
            <>
              <div>
                <label className="block text-xs mb-1">Latitude</label>
                <input
                  value={
                    editData.lat ??
                    editData.position?.lat ??
                    ""
                  }
                  onChange={(e) => setEditData((p) => ({ ...p, lat: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Longitude</label>
                <input
                  value={editData.lng ?? editData.position?.lng ?? ""}
                  onChange={(e) => setEditData((p) => ({ ...p, lng: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
            </>
          )}

          <div className="md:col-span-2">
            <label className="block text-xs mb-1">Description</label>
            <textarea
              rows={3}
              value={editData.description || ""}
              onChange={(e) => setEditData((p) => ({ ...p, description: e.target.value }))}
              className="w-full rounded px-3 py-2 bg-white text-black text-sm"
            />
          </div>

          {c !== "events" && (
            <>
              <div>
                <label className="block text-xs mb-1">Website</label>
                <input
                  value={editData.website || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, website: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Contact email</label>
                <input
                  value={editData.contactEmail || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, contactEmail: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
            </>
          )}

          {(isDirectoryCollection(c) || c === "mapLocations") && (
            <>
              <div>
                <label className="block text-xs mb-1">Contact phone</label>
                <input
                  value={editData.contactPhone || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, contactPhone: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Public address</label>
                <input
                  value={editData.address || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, address: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Postcode</label>
                <input
                  value={editData.postcode || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, postcode: e.target.value }))}
                  className="w-full rounded px-3 py-2 bg-white text-black text-sm"
                />
              </div>
            </>
          )}

          {isDirectoryCollection(c) && (
            <>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={toBool(editData.isLocal)}
                    onChange={(e) => setEditData((p) => ({ ...p, isLocal: e.target.checked }))}
                  />
                  isLocal
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={toBool(editData.promoted)}
                    onChange={(e) => setEditData((p) => ({ ...p, promoted: e.target.checked }))}
                  />
                  promoted
                </label>
              </div>
            </>
          )}

          <div className="md:col-span-2 flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              className="text-xs px-4 py-2 rounded-lg bg-[#E63946] hover:bg-[#f25561] text-white font-semibold"
            >
              Save changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const List = ({ items, collectionName, fields }) => {
    const pending = items.filter((i) => (i.status || "pending") !== "approved");
    const approved = items.filter((i) => (i.status || "pending") === "approved");

    const Row = ({ item }) => (
      <li className="flex justify-between items-start gap-2 border-b border-white/10 pb-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{item.title || item.name || item.fileName}</p>
          {collectionName === "events" && (
            <EventFlyer event={item} compact showFallback={false} className="my-2" eager />
          )}
          {fields(item).map((line, idx) => (
            <p key={idx} className="text-[11px] text-gray-300 break-words">
              {line}
            </p>
          ))}
          <p className="text-[10px] text-gray-500 mt-1">
            Status: {(item.status || "pending")} {item.approvedAt ? `• Approved: ${item.approvedAt}` : ""}
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-end gap-2">
          {(item.status || "pending") !== "approved" && (
            <button
              type="button"
              onClick={() => handleApprove(collectionName, item.id)}
              className="text-[11px] px-2 py-1 rounded-full bg-green-600 text-white"
            >
              Approve
            </button>
          )}
          <button
            type="button"
            onClick={() => startEdit(collectionName, item)}
            className="text-[11px] px-2 py-1 rounded-full bg-blue-600 text-white"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleDelete(collectionName, item.id)}
            className="text-[11px] px-2 py-1 rounded-full bg-red-600 text-white"
          >
            Delete
          </button>
        </div>
      </li>
    );

    return (
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
          <p className="text-xs font-semibold mb-2">
            Pending ({pending.length})
          </p>
          {pending.length === 0 ? (
            <p className="text-xs text-gray-400">No pending items.</p>
          ) : (
            <ul className="space-y-3">{pending.map((i) => <Row key={i.id} item={i} />)}</ul>
          )}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
          <p className="text-xs font-semibold mb-2">
            Approved ({approved.length})
          </p>
          {approved.length === 0 ? (
            <p className="text-xs text-gray-400">No approved items.</p>
          ) : (
            <ul className="space-y-3">{approved.map((i) => <Row key={i.id} item={i} />)}</ul>
          )}
        </div>
      </div>
    );
  };

  
const handleAddPost = async (e) => {
  e.preventDefault();
  const title = newPostTitle.trim();
  const body = newPostBody.trim();
  if (!title || !body) return;

  try {
    setAddingPost(true);
    await addDoc(collection(db, "posts"), {
      title,
      body,
      status: "approved",
      createdAt: new Date().toISOString(),
      createdBy: user ? user.uid : null,
      source: "admin",
    });
    setNewPostTitle("");
    setNewPostBody("");
  } catch (err) {
    console.error("Error adding post", err);
    alert("Add post failed. Check console for details.");
  } finally {
    setAddingPost(false);
  }
};

return (
    <div className="text-white">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold">Admin panel</h1>
        <button
          type="button"
          onClick={handleLogout}
          className="text-xs px-3 py-1 rounded-full bg-white/10 hover:bg-white/20"
        >
          Log out
        </button>
      </div>

      <p className="text-xs text-gray-300 mb-6">
        Approval workflow is enabled: submissions from the public are saved as <span className="font-semibold">pending</span>.
        Approve items to make them visible on the public site. You can also edit or delete.
      </p>

      {COMMUNITY_INFORMATION_AGENT_ENABLED ? (
        <section className="mb-6 rounded-2xl border border-[#ff6b75]/40 bg-[#e63946]/10 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-[#ff9da5]">Community information</p>
          <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Evidence-backed event drafting</h2>
              <p className="mt-1 text-sm text-gray-300">Prepare and edit event drafts, review missing information and possible duplicates, then publish only after explicit approval.</p>
            </div>
            <Link className="rounded-full bg-[#E63946] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#f25561]" to="/admin/community-information">
              Review community information
            </Link>
          </div>
        </section>
      ) : null}

      <section className="mb-6 rounded-2xl border border-[#ff6b75]/40 bg-[#e63946]/10 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-[#ff9da5]">Community Voice</p>
        <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Survey reporting and funding evidence</h2>
            <p className="mt-1 text-sm text-gray-300">Review Festival results, AI and digital skills demand, funding summaries, filters and authorised exports.</p>
          </div>
          <Link className="rounded-full bg-[#E63946] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#f25561]" to="/admin/community-voice/ai-digital-skills">
            Open Community Voice reports
          </Link>
        </div>
      </section>

      <section className="mb-6 rounded-2xl border border-[#ff6b75]/40 bg-[#e63946]/10 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-[#ff9da5]">Phoenix Hub</p>
        <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Venue booking administration</h2>
            <p className="mt-1 text-sm text-gray-300">Review requests, manage pricing and payments, check the calendar, edit spaces and block unavailable time.</p>
          </div>
          <Link className="rounded-full bg-[#E63946] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#f25561]" to="/admin/phoenix-bookings">
            Open Phoenix bookings
          </Link>
        </div>
      </section>

      <section className="mb-6 rounded-2xl border border-[#ff6b75]/40 bg-[#e63946]/10 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-[#ff9da5]">Business Network Launch</p>
        <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Event registration administration</h2>
            <p className="mt-1 text-sm text-gray-300">Search attendees, review campaign sources, manage registration and attendance status, and export authorised CSV records.</p>
          </div>
          <Link className="rounded-full bg-[#E63946] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#f25561]" to="/admin/business-network-registrations">
            Open registrations
          </Link>
        </div>
      </section>

      <section className="mb-6 rounded-2xl border border-[#4dd4c6]/40 bg-[#0aa89a]/10 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-[#7de6dc]">Housing &amp; Development</p>
        <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Events, surveys and partner reporting</h2>
            <p className="mt-1 text-sm text-gray-300">Manage Housing &amp; Development events and surveys, review responses, create QR codes and export authorised reports.</p>
          </div>
          <Link className="rounded-full bg-[#0aa89a] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#078d82]" to="/admin/housing-development">
            Open Housing &amp; Development
          </Link>
        </div>
      </section>

      {/* Editing */}
      {renderEditPanel()}

      {/* Bulk Import */}
      <Card title="Bulk import (CSV)">
        <p className="text-xs text-gray-300 mb-3">
          Upload a CSV to add lots of entries at once. CSV must have headers.
          Choose a target below, then upload your file.
        </p>

        <div className="grid gap-3 md:grid-cols-3 items-end">
          <div>
            <label className="block text-xs mb-1">Import into</label>
            <select
              value={csvTarget}
              onChange={(e) => {
                setCsvTarget(e.target.value);
                setCsvFileName("");
                setCsvPreview([]);
                window.__uunCsvFull = [];
              }}
              className="w-full rounded px-3 py-2 bg-white text-black text-sm"
            >
              <option value="directoryServices">directoryServices</option>
              <option value="mapLocations">mapLocations</option>
              <option value="events">events</option>
            </select>
          </div>

          <div>
            <label className="block text-xs mb-1">CSV file</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => handleCsvFile(e.target.files?.[0])}
              className="w-full text-xs text-gray-200 file:mr-4 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#E63946] file:text-white hover:file:bg-[#f25561]"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={csvImportAsApproved}
                onChange={(e) => setCsvImportAsApproved(e.target.checked)}
              />
              Import as approved
            </label>
            <button
              type="button"
              onClick={runCsvImport}
              disabled={!csvPreview.length || importing}
              className="ml-auto text-xs px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60"
            >
              {importing ? "Importing..." : "Import"}
            </button>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-gray-300">
          <p className="font-semibold">CSV headers expected</p>
          {csvTarget === "directoryServices" && (
            <p>
              name, description, type, website, contactEmail, contactPhone, isLocal, promoted
            </p>
          )}
          {csvTarget === "mapLocations" && (
            <p>
              name, description, keywords, website, contactEmail, lat, lng
            </p>
          )}
          {csvTarget === "events" && (
            <p>
              name, description, date, time
            </p>
          )}
        </div>

        {csvFileName && (
          <p className="mt-3 text-xs text-gray-400">Loaded: {csvFileName} • Previewing first {csvPreview.length} rows</p>
        )}

        {csvPreview.length > 0 && (
          <div className="mt-3 max-h-56 overflow-auto border border-white/10 rounded-lg">
            <table className="w-full text-[11px]">
              <thead className="bg-white/5">
                <tr>
                  {Object.keys(csvPreview[0]).map((k) => (
                    <th key={k} className="text-left p-2 border-b border-white/10">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvPreview.map((row, idx) => (
                  <tr key={idx} className="border-b border-white/5">
                    {Object.keys(csvPreview[0]).map((k) => (
                      <td key={k} className="p-2 align-top">
                        {typeof row[k] === "object" ? JSON.stringify(row[k]) : String(row[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-6 mt-6">
        <Card title={`Events (pending: ${pendingCounts.events})`}>
          <List
            items={events}
            collectionName="events"
            fields={(ev) => [
              ev.description || "",
              `Date: ${ev.date || ""} • Time: ${ev.time || ""}`,
              (ev.flyerFileName || ev.fileName) ? `Flyer: ${ev.flyerFileName || ev.fileName}` : "No flyer supplied",
            ].filter(Boolean)}
          />
        </Card>

        <Card title={`Directory services (pending: ${pendingCounts.services})`}>
          <List
            items={services}
            collectionName="directoryServices"
            fields={(s) => [
              s.description || "",
              `Type: ${s.type || ""}`,
              s.website ? `Website: ${s.website}` : "",
              s.contactEmail ? `Email: ${s.contactEmail}` : "",
              s.contactPhone ? `Phone: ${s.contactPhone}` : "",
            ].filter(Boolean)}
          />
        </Card>

        <Card title={`Directory submissions awaiting review (pending: ${pendingCounts.serviceSubmissions})`}>
          <p className="text-xs text-gray-300 mb-3">
            Listings submitted from the public Directory page remain hidden until an administrator approves them.
          </p>
          <List
            items={serviceSubmissions}
            collectionName="directorySubmissions"
            fields={(service) => [
              service.description || "",
              `Type: ${service.type || ""}`,
              service.address ? `Address: ${service.address}` : "",
              service.postcode ? `Postcode: ${service.postcode}` : "",
              service.website ? `Website: ${service.website}` : "",
              service.contactEmail ? `Email: ${service.contactEmail}` : "",
              service.contactPhone ? `Phone: ${service.contactPhone}` : "",
            ].filter(Boolean)}
          />
        </Card>

        <Card title={`Map locations (pending: ${pendingCounts.locations})`}>
          <List
            items={locations.map((l) => ({
              ...l,
              name: l.name,
              lat: l.position?.lat,
              lng: l.position?.lng,
            }))}
            collectionName="mapLocations"
            fields={(loc) => [
              loc.description || "",
              loc.keywords ? `Keywords: ${loc.keywords}` : "",
              loc.address ? `Address: ${loc.address}` : "",
              loc.postcode ? `Postcode: ${loc.postcode}` : "",
              (loc.position && typeof loc.position.lat === "number")
                ? `Lat/Lng: ${loc.position.lat}, ${loc.position.lng}`
                : "",
              loc.website ? `Website: ${loc.website}` : "",
              loc.contactEmail ? `Email: ${loc.contactEmail}` : "",
              loc.contactPhone ? `Phone: ${loc.contactPhone}` : "",
            ].filter(Boolean)}
          />
        </Card>

        <Card title={`Event documents (pending: ${pendingCounts.docs})`}>
          <List
            items={docs.map((d) => ({ ...d, name: d.fileName }))}
            collectionName="eventDocuments"
            fields={(d) => [
              d.eventName ? `Event: ${d.eventName}` : "",
              d.url ? `URL: ${d.url}` : "",
            ].filter(Boolean)}
          />
        </Card>

        <Card title={`Food bank updates (pending: ${pendingCounts.food})`}>
          <p className="text-xs text-gray-300 mb-3">
            Create updates by adding documents to the <span className="font-semibold">foodBankUpdates</span> collection
            (status: pending). Approve to publish on the public Food Bank page.
          </p>
          <List
            items={foodUpdates.map((u) => ({ ...u, name: u.title || "Update" }))}
            collectionName="foodBankUpdates"
            fields={(u) => [
              u.date ? `Date: ${u.date}` : "",
              u.message ? u.message : (u.content || ""),
            ].filter(Boolean)}
          />
        </Card>

<Card title={`Community posts (pending: ${pendingCounts.posts})`}>
  <p className="text-xs text-gray-300 mb-3">
    Posts are stored in the <span className="font-semibold">posts</span> collection. Public submissions should be saved as <span className="font-semibold">pending</span> and can be approved here.
  </p>

  <form onSubmit={handleAddPost} className="bg-white/5 border border-white/10 rounded-xl p-3 mb-4">
    <p className="text-xs font-semibold mb-2">Add a new post (publishes immediately)</p>
    <input
      value={newPostTitle}
      onChange={(e) => setNewPostTitle(e.target.value)}
      placeholder="Title"
      className="w-full mb-2 text-xs px-3 py-2 rounded-lg bg-black/30 border border-white/10"
      required
    />
    <textarea
      value={newPostBody}
      onChange={(e) => setNewPostBody(e.target.value)}
      placeholder="Body"
      rows={4}
      className="w-full mb-2 text-xs px-3 py-2 rounded-lg bg-black/30 border border-white/10"
      required
    />
    <button
      type="submit"
      disabled={addingPost}
      className="text-xs px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60"
    >
      {addingPost ? "Posting..." : "Publish"}
    </button>
  </form>

  <List
    items={posts.map((p) => ({ ...p, name: p.title || "Post" }))}
    collectionName="posts"
    fields={(p) => [
      p.body || "",
      p.createdAt ? `Created: ${p.createdAt}` : "",
    ].filter(Boolean)}
  />
</Card>

      </div>
    </div>
  );
}
